import axiosInstance from "../../../middlewares/axiosInstance";
import moment from "moment";

export interface DashboardStats {
  total_sales: number;
  total_orders: number;
  active_customers: number;
  inventory_value: number;
  trends: {
    sales: number;
    orders: number;
    customers: number;
    inventory: number;
  };
}

export interface SalesData {
  date: string;
  sales: number;
  orders: number;
}

export interface LowStockItem {
  id: string;
  name: string;
  stock_quantity: number;
  minStockLevel: number;
  maxStockLevel: number;
  lastRestockDate: string;
  unit: string;
}

export interface DashboardOrder {
  id: string;
  order_date: string;
  customer_name: string;
  total_amount: number;
  status: 'completed' | 'pending' | 'cancelled';
  payment_method: string;
}

export interface DashboardNotification {
  id: string;
  type: 'warning' | 'success' | 'info' | 'error';
  message: string;
  timestamp: string;
  isRead: boolean;
}

export interface SalesStatsResponse {
  success: boolean;
  data?: {
    total_sales: number;
    average_sales: number;
    total_revenue: number;
    total_orders: number;
  };
  error?: {
    code: string;
    message: string;
    details?: any;
  };
}

export interface SalesOverTimeResponse {
  success: boolean;
  data: {
    sales_data: Array<{
      date: string;
      total_sales: number;
    }>;
  };
}

export interface RevenueOverTimeResponse {
  success: boolean;
  data: {
    revenue_data: Array<{
      date: string;
      total_revenue: number;
    }>;
  };
}

export interface OrdersOverTimeResponse {
  success: boolean;
  data: {
    orders_data: Array<{
      date: string;
      total_orders: number;
    }>;
  };
}

export interface TopPerformersResponse {
  success: boolean;
  data: {
    top_selling_items: Array<{
      name: string;
      count: number;
    }>;
    top_revenue_items: Array<{
      name: string;
      count: number;
      revenue: number;
    }>;
    top_cashiers: Array<{
      name: string;
      total_orders: number;
    }>;
  };
}

// Validation functions
const validateStats = (data: any): DashboardStats => {
  return {
    total_sales: Number(data?.total_sales) || 0,
    total_orders: Number(data?.total_orders) || 0,
    active_customers: Number(data?.active_customers) || 0,
    inventory_value: Number(data?.inventory_value) || 0,
    trends: {
      sales: Number(data?.trends?.sales) || 0,
      orders: Number(data?.trends?.orders) || 0,
      customers: Number(data?.trends?.customers) || 0,
      inventory: Number(data?.trends?.inventory) || 0,
    },
  };
};

const validateSalesData = (data: any[]): SalesData[] => {
  if (!Array.isArray(data)) return [];
  
  return data.map(item => ({
    date: item?.date || moment().format('MMM DD'),
    sales: Number(item?.sales) || 0,
    orders: Number(item?.orders) || 0,
  }));
};

const validateOrders = (data: any[]): DashboardOrder[] => {
  if (!Array.isArray(data)) return [];

  return data.map(order => ({
    id: order?.id || '',
    order_date: order?.order_date || moment().toISOString(),
    customer_name: order?.customer_name || 'Unknown Customer',
    total_amount: Number(order?.total_amount) || 0,
    status: ['completed', 'pending', 'cancelled'].includes(order?.status) 
      ? order.status 
      : 'pending',
    payment_method: order?.payment_method || 'unknown',
  }));
};

const validateLowStockItems = (data: any[]): LowStockItem[] => {
  if (!Array.isArray(data)) return [];

  return data.map(item => ({
    id: item?.id || '',
    name: item?.name || 'Unknown Product',
    stock_quantity: Number(item?.stock_quantity) || 0,
    minStockLevel: Number(item?.minStockLevel) || 0,
    maxStockLevel: Number(item?.maxStockLevel) || 100,
    lastRestockDate: item?.lastRestockDate || moment().subtract(1, 'day').toISOString(),
    unit: item?.unit || 'pcs',
  }));
};

const validateNotifications = (data: any[]): DashboardNotification[] => {
  if (!Array.isArray(data)) return [];

  return data.map(notification => ({
    id: notification?.id || '',
    type: ['warning', 'success', 'info', 'error'].includes(notification?.type)
      ? notification.type
      : 'info',
    message: notification?.message || 'No message available',
    timestamp: notification?.timestamp || moment().toISOString(),
    isRead: Boolean(notification?.isRead),
  }));
};

export class DashboardService {
  async getStats(): Promise<DashboardStats> {
    try {
      const response = await axiosInstance.post('/business-admin/dashboard/stats');
      return validateStats(response.data.data);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      return validateStats({});
    }
  }

  async getSalesData(days: number = 7): Promise<SalesData[]> {
    try {
      const response = await axiosInstance.post('/business-admin/dashboard/sales', {
        startDate: moment().subtract(days - 1, 'days').startOf('day').valueOf(),
        endDate: moment().endOf('day').valueOf(),
      });
      return validateSalesData(response.data.data);
    } catch (error) {
      console.error('Error fetching sales data:', error);
      return [];
    }
  }

  async getRecentOrders(limit: number = 5): Promise<DashboardOrder[]> {
    try {
      const response = await axiosInstance.post('/business-admin/dashboard/recent-orders', {
        limit,
      });
      return validateOrders(response.data.data);
    } catch (error) {
      console.error('Error fetching recent orders:', error);
      return [];
    }
  }

  async getLowStockItems(): Promise<LowStockItem[]> {
    try {
      const response = await axiosInstance.post('/business-admin/dashboard/low-stock');
      return validateLowStockItems(response.data.data);
    } catch (error) {
      console.error('Error fetching low stock items:', error);
      return [];
    }
  }

  async getNotifications(limit: number = 5): Promise<DashboardNotification[]> {
    try {
      const response = await axiosInstance.post('/business-admin/dashboard/notifications', {
        limit,
      });
      return validateNotifications(response.data.data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      return [];
    }
  }

  async getSalesStats(startDate: Date, endDate: Date, storeId: string): Promise<SalesStatsResponse["data"]> {
    try {
      const response = await axiosInstance.post<SalesStatsResponse>(
        "/business-admin/dashboard/sales-stats",
        {
          store_id: storeId,
        }
      );

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.error?.message || "Failed to fetch sales stats");
      }
    } catch (error) {
      console.error("Error fetching sales stats:", error);
      throw error;
    }
  }

  async getSalesOverTime(
    storeId: string,
    startDate?: Date,
    endDate?: Date
  ): Promise<SalesOverTimeResponse["data"]["sales_data"]> {
    try {
      const response = await axiosInstance.post<SalesOverTimeResponse>(
        "/business-admin/dashboard/sales-over-time",
        {
          store_id: storeId
        }
      );

      if (response.data.success) {
        return response.data.data.sales_data;
      } else {
        throw new Error("Failed to fetch sales over time data");
      }
    } catch (error) {
      console.error("Error fetching sales over time:", error);
      throw error;
    }
  }

  async getRevenueOverTime(
    storeId: string,
    startDate?: Date,
    endDate?: Date
  ): Promise<RevenueOverTimeResponse["data"]["revenue_data"]> {
    try {
      const response = await axiosInstance.post<RevenueOverTimeResponse>(
        "/business-admin/dashboard/revenue-over-time",
        {
          store_id: storeId
        }
      );

      if (response.data.success) {
        return response.data.data.revenue_data;
      } else {
        throw new Error("Failed to fetch revenue over time data");
      }
    } catch (error) {
      console.error("Error fetching revenue over time:", error);
      throw error;
    }
  }

  async getOrdersOverTime(
    storeId: string,
    startDate?: Date,
    endDate?: Date
  ): Promise<OrdersOverTimeResponse["data"]["orders_data"]> {
    try {
      const response = await axiosInstance.post<OrdersOverTimeResponse>(
        "/business-admin/dashboard/orders-over-time",
        {
          store_id: storeId
        }
      );

      if (response.data.success) {
        return response.data.data.orders_data;
      } else {
        throw new Error("Failed to fetch orders over time data");
      }
    } catch (error) {
      console.error("Error fetching orders over time:", error);
      throw error;
    }
  }

  async getTopPerformers(
    storeId: string,
    startDate?: Date,
    endDate?: Date
  ): Promise<TopPerformersResponse["data"]> {
    try {
      const response = await axiosInstance.post<TopPerformersResponse>(
        "/business-admin/dashboard/top-performers",
        {
          store_id: storeId
        }
      );

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error("Failed to fetch top performers data");
      }
    } catch (error) {
      console.error("Error fetching top performers:", error);
      throw error;
    }
  }
}

export const dashboardService = new DashboardService(); 